'use client';

import { FC, lazy, useState } from 'react';

import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

const AddressDetailFlyout = lazy(() => import('./address-detail-flyout'));

interface Props {
   addresses: Array<Address>;
   isConsumer: boolean;
}

export const AddressList: FC<Props> = ({ addresses, isConsumer }) => {
   const translate = useTranslations();
   const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
   const [addressDetailId, setAddressDetailId] = useState<number | undefined>(undefined);

   const handleFlyoutOpen = (addressId: Address['id']) => {
      setAddressDetailId(addressId);
      setIsFlyoutOpen(true);
   };

   const onFlyoutClose = () => {
      setAddressDetailId(undefined);
      setIsFlyoutOpen(false);
   };

   if (addresses.length === 0) {
      return (
         <Typography color="subtle" variant="small">
            {translate('addresses.no-addresses-found')}
         </Typography>
      );
   }

   const addressList = addresses.map(({ id, name, countryName, type, line1, line2, line3, line4, line5 }) => {
      const addressLines = [line1, line2, line3, line4, line5]
         .filter(Boolean)
         .map((line, index, arr) => (index === arr.length - 1 ? line : `${line}, `));

      return (
         <Box key={id + type} onClick={() => handleFlyoutOpen(id)}>
            <Paper width="100%" data-testid="address__list-item" shadow>
               <Box direction="row">
                  <Typography data-testid="address__name" component="h3" variant="body-highlight">
                     {name}
                  </Typography>
                  <Badge variant="info" data-testid="address__type">
                     {translate(`addresses.types.${type === 'Legal Address' ? 'legal-address' : 'delivery-address'}`)}
                  </Badge>
               </Box>

               <Typography data-testid="address__lines" variant="body">
                  {addressLines} - {countryName}
               </Typography>
            </Paper>
         </Box>
      );
   });

   return (
      <>
         <Box direction="column">{addressList}</Box>
         {isConsumer && <AddressDetailFlyout open={isFlyoutOpen} onFlyoutClose={onFlyoutClose} addressId={addressDetailId} />}
      </>
   );
};
